/**
 * ч.1101 (рішення власника): аудит проводок ПЕРЕД закриттям місяця.
 * На сторінці закриття бухгалтер бачить, чи перевіряли проводки цього місяця
 * і скільки відкритих знахідок, і може запустити перевірку тут же. Закриття
 * НЕ блокується — це порада: рішення лишається за бухгалтером.
 * Перевірка — правила без моделі: кредитів не витрачає, облік не змінює.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useToast } from '@/components/ToastProvider';
import { postingAudit, type AuditPeriod } from '@/api/postingAudit';
import { useAllPermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { MONTHS_UK } from '@/shared/components/PeriodFilter';
import { extractApiError } from '@/utils/extractApiError';

/** Перший і останній день місяця у форматі YYYY-MM-DD. */
export function monthPeriod(year: number, month: number): AuditPeriod {
  const mm = String(month).padStart(2, '0');
  const last = new Date(year, month, 0).getDate();
  return { date_from: `${year}-${mm}-01`, date_to: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}

export default function AuditBeforeClose({ orgId, year, month }: { orgId: string; year: number; month: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { permissionsByOrg } = useAllPermissionsByOrg();
  const canView = (permissionsByOrg[orgId] ?? []).includes('accounting.audit.view');
  const period = monthPeriod(year, month);
  const q = useQuery({
    queryKey: ['posting-audit', 'summary', orgId, period.date_from, period.date_to, 'close'],
    queryFn: () => postingAudit.summary(orgId, period),
    enabled: !!orgId && canView,
  });
  const run = useMutation({
    mutationFn: () => postingAudit.run(orgId, period),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posting-audit'] }),
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося перевірити проводки') }),
  });
  if (!orgId || !canView) return null;

  const s = q.data;
  const sev = s?.open_by_severity;
  const open = sev ? sev.ERROR + sev.WARN + sev.INFO : 0;
  const checked = !!s?.covering_run;
  const tone = !checked ? 'info' : sev && sev.ERROR > 0 ? 'warn' : open > 0 ? 'info' : 'ok';
  const label = `${MONTHS_UK[month] ?? month} ${year}`;
  const link = `/audit/postings?org=${orgId}&from=${period.date_from}&to=${period.date_to}`;

  return (
    <div className={`mc__panel mc__panel--${tone}`} data-testid="audit-before-close">
      <div className="mc__panel-head">
        Аудит проводок за {label.toLowerCase()}
        <button type="button" disabled={run.isPending || q.isLoading}
          title="Перевірити проводки цього місяця на типові помилки. Облік не змінюється, кредити не витрачаються"
          onClick={() => run.mutate()}>
          {run.isPending ? 'перевіряємо…' : checked ? 'перевірити ще раз' : 'перевірити проводки'}
        </button>
        {checked && <Link to={link} className="an__link">відкрити знахідки</Link>}
      </div>
      {q.isLoading && <div>Завантажуємо…</div>}
      {s && !checked && (
        <div>
          Проводки цього місяця ще не перевірялись. Радимо перевірити перед закриттям: після
          закриття виправляти помилки складніше.
          {open > 0 && ` З раніших перевірок за цей місяць лишилось відкритих знахідок: ${open}.`}
        </div>
      )}
      {s && checked && sev && (
        <div>
          {open === 0
            ? 'Відкритих знахідок немає — проводки місяця в порядку.'
            : <>Відкритих знахідок: <b>{open}</b> — помилок {sev.ERROR}, уваги {sev.WARN}, до відома {sev.INFO}.
              {sev.ERROR > 0
                ? ' Помилки радимо виправити до закриття місяця.'
                : ' Критичних помилок немає; перегляньте решту на свій розсуд.'}</>}
          <span style={{ color: 'var(--muted)', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
            перевірено {new Date(s.covering_run!.created_at).toLocaleString('uk-UA')}
          </span>
        </div>
      )}
    </div>
  );
}
